import { useEffect, useState } from 'react';
import {
  createExpert,
  createExpertAssignment,
  deleteExpertAssignment,
  deactivateExpert,
  getExperts,
  resendExpertInvitation,
} from '../../services/ExpertReviewService';
import { getInterviews } from '../../services/Interviews/interviewService';

export default function ExpertManagement() {
  const [experts, setExperts] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '' });
  const [assignment, setAssignment] = useState({ expert: '', interview: '' });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = () =>
    Promise.all([getExperts(), getInterviews()])
      .then(([expertData, interviewData]) => {
        setExperts(expertData.results || expertData);
        setInterviews(interviewData.results || interviewData);
      })
      .catch(() => setError('Unable to load experts and interviews.'));

  useEffect(() => {
    load();
  }, []);

  const invite = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const result = await createExpert(form);
      setMessage(
        result.invitation_sent
          ? 'Expert created and invitation sent.'
          : `Expert created, but email failed: ${result.invitation_error}`,
      );
      setForm({ email: '', first_name: '', last_name: '' });
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.email?.[0] || 'Unable to create expert.');
    }
  };

  const assign = async (event) => {
    event.preventDefault();
    try {
      await createExpertAssignment(assignment);
      setMessage('Expert assigned to interview.');
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.non_field_errors?.[0] || 'Unable to assign expert.');
    }
  };

  const unassign = async (assignmentUuid) => {
    try {
      await deleteExpertAssignment(assignmentUuid);
      setMessage('Interview assignment removed.');
      await load();
    } catch (requestError) {
      setError('Unable to remove assignment.');
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Human experts</h1>
        <p className="text-gray-600 dark:text-gray-300">Invite reviewers and grant access to specific interviews.</p>
      </div>
      {message && <p className="rounded bg-green-100 p-3 text-green-800">{message}</p>}
      {error && <p className="rounded bg-red-100 p-3 text-red-800">{error}</p>}

      <form onSubmit={invite} className="grid gap-3 rounded-xl border bg-white p-5 md:grid-cols-4 dark:bg-gray-800 dark:border-gray-700">
        <input required className="rounded border p-2 text-gray-900" placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        <input required className="rounded border p-2 text-gray-900" placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        <input required type="email" className="rounded border p-2 text-gray-900" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <button className="rounded bg-blue-600 px-4 py-2 text-white">Invite expert</button>
      </form>

      <form onSubmit={assign} className="grid gap-3 rounded-xl border bg-white p-5 md:grid-cols-3 dark:bg-gray-800 dark:border-gray-700">
        <select required className="rounded border p-2 text-gray-900" value={assignment.expert} onChange={(e) => setAssignment({ ...assignment, expert: e.target.value })}>
          <option value="">Choose expert</option>
          {experts.filter((expert) => expert.is_active).map((expert) => <option key={expert.uuid} value={expert.uuid}>{expert.first_name} {expert.last_name}</option>)}
        </select>
        <select required className="rounded border p-2 text-gray-900" value={assignment.interview} onChange={(e) => setAssignment({ ...assignment, interview: e.target.value })}>
          <option value="">Choose interview</option>
          {interviews.map((interview) => <option key={interview.uuid} value={interview.uuid}>{interview.name}</option>)}
        </select>
        <button className="rounded bg-blue-600 px-4 py-2 text-white">Assign interview</button>
      </form>

      <div className="grid gap-4">
        {experts.map((expert) => (
          <article key={expert.uuid} className="rounded-xl border bg-white p-5 dark:bg-gray-800 dark:border-gray-700">
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <h2 className="font-semibold">{expert.first_name} {expert.last_name}</h2>
                <p>{expert.email} · {expert.is_active ? 'Active' : 'Invitation pending / inactive'}</p>
                <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-600 dark:text-gray-300">
                  {expert.assignments.filter((item) => item.is_active).map((item) => (
                    <span key={item.uuid} className="rounded border px-2 py-1">
                      {item.interview_name}{' '}
                      <button type="button" onClick={() => unassign(item.uuid)} className="text-red-600" aria-label={`Remove ${item.interview_name} assignment`}>x</button>
                    </span>
                  ))}
                  {!expert.assignments.some((item) => item.is_active) && <span>Assignments: None</span>}
                </div>
              </div>
              <div className="flex gap-2">
                {!expert.is_active && <button onClick={() => resendExpertInvitation(expert.uuid).then(() => setMessage('Invitation resent.')).catch(() => setError('Unable to resend invitation.'))} className="rounded border px-3 py-2">Resend invite</button>}
                {expert.is_active && <button onClick={() => deactivateExpert(expert.uuid).then(load)} className="rounded bg-red-600 px-3 py-2 text-white">Deactivate</button>}
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
